<!-- Conduit screens, re-homed inside the Outbound Dashboard's tab strip.
     No outer nav / brand selector here — the dashboard owns those. Each mounted
     instance renders only the screens belonging to its `section` input. -->
<div class="cdt cdt--embedded">
    <div class="cdt__main">
        <div class="cdt__page">

        <!-- Back-to-list affordance for drill-in screens within a section's flow -->
        <button *ngIf="showBackToList"
                type="button"
                class="cdt__btn cdt__btn--ghost cdt__btn--sm cdt__back"
                (click)="go(sectionListScreen)">
            <i class="fa-solid fa-arrow-left"></i> Back to {{ sectionListLabel }}
        </button>

        <!-- ════ SECTION: BINDINGS — list ════ -->
        <section *ngIf="section === 'bindings' && screen === 'bindings'" class="cdt__screen">
            <div class="cdt__eyebrow">Audience pipeline</div>
            <h1 class="cdt__title">Bindings</h1>
            <p class="cdt__lede">Each binding keeps one Reply sequence in lockstep with one AIDP contact view. People who match the view are enrolled; people who stop matching are removed. Nothing sends without a binding being live.</p>

            <div class="cdt__gaugewide">
                <div class="cdt__lockstep">
                    <i class="fa-solid fa-water cdt__lockstep-ic"></i>
                    <div class="cdt__lockstep-read">
                        <b>Lockstep across loaded bindings</b><br>
                        {{ lockstepSummary.liveBindings }} live binding(s)
                    </div>
                </div>
                <div class="cdt__spacer"></div>
                <button class="cdt__btn cdt__btn--primary" (click)="go('bind')"><i class="fa-solid fa-link"></i> Bind a view to a sequence</button>
            </div>

            <div *ngIf="bindingsState.status === 'loading'" class="cdt__state"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading bindings…</div>
            <div *ngIf="bindingsState.status === 'not-deployed'" class="cdt__state cdt__state--info">
                <i class="fa-solid fa-plug-circle-xmark"></i>
                <div><strong>Conduit actions not deployed here</strong><p>{{ bindingsState.message }}</p></div>
            </div>
            <div *ngIf="bindingsState.status === 'error'" class="cdt__state cdt__state--error">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <div><strong>Couldn't load bindings</strong><p>{{ bindingsState.message }}</p></div>
            </div>

            <div class="cdt__tablewrap" *ngIf="bindingsState.status === 'ready'">
                <div *ngIf="bindings.length === 0" class="cdt__state">
                    <i class="fa-solid fa-link-slash"></i>
                    <div><strong>No bindings yet</strong><p>Build a contact view in the Audiences tab, then bind it to a Reply sequence to get started.</p></div>
                </div>
                <table *ngIf="bindings.length > 0">
                    <thead>
                        <tr><th>Binding</th><th>Last sync Δ</th><th>Mode</th><th>Frequency</th><th>Last run</th><th>Status</th><th></th></tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let b of bindings">
                            <td>
                                <span class="cdt__binding-name">{{ b.name || '(unnamed binding)' }}</span>
                                <span class="cdt__binding-flow">
                                    <span>{{ b.view || 'no view' }}</span>
                                    <span class="cdt__arr">→</span>
                                    <span>{{ b.externalSequenceName || ('Reply seq ' + (b.externalSequenceId || '—')) }}</span>
                                </span>
                            </td>
                            <td class="cdt__kv">
                                <span class="cdt__chip cdt__chip--ok" *ngIf="(b.lastSyncAddedCount||0) > 0">+{{ b.lastSyncAddedCount }}</span>
                                <span class="cdt__chip cdt__chip--bad" *ngIf="(b.lastSyncRemovedCount||0) > 0">−{{ b.lastSyncRemovedCount }}</span>
                                <span *ngIf="!(b.lastSyncAddedCount||b.lastSyncRemovedCount)" class="cdt__mut">—</span>
                            </td>
                            <td>
                                <span class="cdt__chip" [class.cdt__chip--flow]="b.mode === 'Live'" [class.cdt__chip--paused]="b.mode !== 'Live'">
                                    {{ b.mode || 'DryRun' }}
                                </span>
                            </td>
                            <td class="cdt__tiny cdt__mut">{{ freqLabel(b.syncFrequencyHours) }}</td>
                            <td class="cdt__tiny cdt__mut">{{ b.lastSyncAt ? (b.lastSyncAt | date:'short') : 'never' }}</td>
                            <td><span class="cdt__chip cdt__chip--{{ statusChip(b.lastSyncStatus) }}">{{ b.lastSyncStatus || 'not run' }}</span></td>
                            <td>
                                <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" (click)="openDryRun(b)">Dry run</button>
                                <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" (click)="go('history')">History</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="cdt__tiny cdt__mut cdt__legend">
                <span class="cdt__chip cdt__chip--flow">Live</span> applies to Reply on the next run ·
                <span class="cdt__chip cdt__chip--paused">DryRun</span> previews only, no Reply writes ·
                <span class="cdt__chip cdt__chip--bad">Failed</span> a sync errored — see Run history.
            </p>
        </section>

        <!-- ════ SECTION: AUDIENCES — contact views list ════ -->
        <section *ngIf="section === 'audiences' && screen === 'views'" class="cdt__screen">
            <div class="cdt__eyebrow">Audiences</div>
            <h1 class="cdt__title">Contact views</h1>
            <p class="cdt__lede">A view is the logical definition of an audience, drawn live from AIDP. Define it once, verify it, and bind it to a sequence. The view is always the source of truth — the sequence follows it.</p>
            <div class="cdt__rowbtns">
                <button class="cdt__btn cdt__btn--primary" (click)="openBuilderForNew()"><i class="fa-solid fa-plus"></i> New contact view</button>
            </div>
            <div *ngIf="contactViewsState.status === 'loading'" class="cdt__state"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading views…</div>
            <div *ngIf="contactViewsState.status === 'not-deployed'" class="cdt__state cdt__state--info"><i class="fa-solid fa-plug-circle-xmark"></i><div><strong>Conduit actions not deployed here</strong><p>{{ contactViewsState.message }}</p></div></div>
            <div *ngIf="contactViewsState.status === 'error'" class="cdt__state cdt__state--error"><i class="fa-solid fa-triangle-exclamation"></i><div><strong>Couldn't load views</strong><p>{{ contactViewsState.message }}</p></div></div>
            <div class="cdt__tablewrap" *ngIf="contactViewsState.status === 'ready'">
                <div *ngIf="distinctViews.length === 0" class="cdt__state">
                    <i class="fa-solid fa-table-cells"></i>
                    <div><strong>No contact views yet</strong><p>Build one in the View builder — saved Smart-Filter views over Contacts show up here.</p></div>
                </div>
                <table *ngIf="distinctViews.length > 0">
                    <thead><tr><th>View</th><th>Used by</th><th></th></tr></thead>
                    <tbody>
                        <tr *ngFor="let v of distinctViews">
                            <td><b>{{ v.view }}</b><div class="cdt__tiny cdt__mut">{{ v.viewId }}</div></td>
                            <td class="cdt__tiny">{{ v.bindingCount }} binding(s)</td>
                            <td>
                                <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" (click)="builderViewId = v.viewId; builderName = v.view; go('builder'); generateBuilderDraft()">Open</button>
                                <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" title="Open in MJ Data Explorer to inspect/verify the generated SQL" (click)="openViewInDataExplorer(v.viewId)"><i class="fa-solid fa-up-right-from-square"></i> Data Explorer</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <!-- ════ SECTION: AUDIENCES — view builder (Smart Filter) ════ -->
        <section *ngIf="section === 'audiences' && screen === 'builder'" class="cdt__screen">
            <div class="cdt__eyebrow">Audiences · view builder</div>
            <h1 class="cdt__title">View builder</h1>
            <p class="cdt__lede">Describe the audience in plain language and hit <b>Generate</b>. Conduit's Smart Filter drafts the SQL, shows you exactly who matches, and lets you review and edit the precise WHERE clause before saving. You verify; nobody syncs until you do.</p>

            <div class="cdt__builder">
                <!-- left: smart filter -->
                <div>
                    <div class="cdt__card cdt__pad">
                        <div class="cdt__card-head">
                            <b>Smart filter</b>
                            <span class="cdt__chip cdt__chip--flow">Source: AIDP · Contacts</span>
                        </div>

                        <label class="cdt__label">View name</label>
                        <input class="cdt__input" [(ngModel)]="builderName" placeholder="e.g. Assoc · 10k–20k members">

                        <label class="cdt__label">Describe this audience</label>
                        <textarea class="cdt__textarea" rows="4" [(ngModel)]="builderPrompt"
                                  placeholder="e.g. US associations with between 10,000 and 20,000 members that have a deliverable email and aren't on do-not-contact"></textarea>

                        <!-- The "Verify SQL" disclosure — EDITABLE once a draft exists. -->
                        <div class="cdt__adv">
                            <button type="button" class="cdt__adv-summary" (click)="showSql = !showSql">
                                <i class="fa-solid" [class.fa-chevron-right]="!showSql" [class.fa-chevron-down]="showSql"></i>
                                <span>Verify SQL</span>
                                <span class="cdt__adv-tag">Advanced · editable</span>
                            </button>
                            <div *ngIf="showSql" class="cdt__adv-body">
                                <div *ngIf="!builderGenerated" class="cdt__sqlnote cdt__mut">
                                    Hit <b>Generate</b> to draft the WHERE clause, then review and edit it here.
                                </div>
                                <ng-container *ngIf="builderGenerated">
                                    <label class="cdt__label">WHERE clause <span class="cdt__mut">(review &amp; edit before saving)</span></label>
                                    <textarea class="cdt__textarea cdt__sqlbox" rows="4" spellcheck="false"
                                              [(ngModel)]="builderEditableSql"
                                              placeholder="AccountID IN (SELECT ID FROM crm.vwAccounts WHERE Subsection = '6')"></textarea>
                                    <div class="cdt__rowbtns">
                                        <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" [disabled]="builderPreviewing || !builderEditableSql" (click)="previewEditedClause()">
                                            <i class="fa-solid fa-rotate" [class.fa-spin]="builderPreviewing"></i> Re-preview this clause
                                        </button>
                                        <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" *ngIf="builderSqlEdited" [disabled]="builderPreviewing" (click)="builderEditableSql = builderGeneratedSql">
                                            <i class="fa-solid fa-arrow-rotate-left"></i> Reset to generated
                                        </button>
                                    </div>
                                    <div *ngIf="builderSqlEdited" class="cdt__sqlnote cdt__mut">
                                        <i class="fa-solid fa-pen"></i> Edited — your clause will be saved instead of the generated one.
                                    </div>
                                    <div *ngIf="builderExplanation" class="cdt__sqlnote">
                                        <i class="fa-solid fa-circle-info"></i> {{ builderExplanation }}
                                    </div>
                                </ng-container>
                            </div>
                        </div>
                    </div>

                    <div class="cdt__rowbtns">
                        <button class="cdt__btn cdt__btn--primary" [disabled]="builderPreviewing || (!builderViewId && !builderPrompt)" (click)="generateBuilderDraft()">
                            <i class="fa-solid fa-wand-magic-sparkles"></i>
                            <i *ngIf="builderPreviewing" class="fa-solid fa-circle-notch fa-spin"></i>
                            Generate
                        </button>
                        <button class="cdt__btn" [disabled]="builderSaving || !builderName || !builderPrompt || !builderGenerated" (click)="saveBuilderView()">
                            <i class="fa-solid fa-floppy-disk"></i>
                            <i *ngIf="builderSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                            Save view
                        </button>
                    </div>
                    <p class="cdt__tiny cdt__mut" *ngIf="builderSavedViewId">Saved view <span class="cdt__kv">{{ builderSavedViewId }}</span>. Bind it from the <b>Bindings</b> tab using this id.</p>
                    <p class="cdt__error" *ngIf="builderError"><i class="fa-solid fa-triangle-exclamation"></i> {{ builderError }}</p>
                </div>

                <!-- right: who matches -->
                <div>
                    <div class="cdt__card cdt__pad">
                        <div class="cdt__previewhead">
                            <span class="cdt__cnt">{{ builderPreview ? builderPreview.totalCount : '—' }}</span>
                            <span class="cdt__mut">people match</span>
                            <button class="cdt__btn cdt__btn--ghost cdt__btn--sm" [disabled]="builderPreviewing || (!builderViewId && !builderPrompt)" (click)="generateBuilderDraft()">
                                <i class="fa-solid fa-rotate" [class.fa-spin]="builderPreviewing"></i> Refresh
                            </button>
                        </div>
                        <p class="cdt__tiny cdt__mut">Live from AIDP. Spot-check before binding.</p>

                        <div class="cdt__matchlist" *ngIf="builderPreview && builderPreview.sample.length > 0">
                            <div class="cdt__matchrow" *ngFor="let c of builderPreview.sample">
                                <div class="cdt__mav">{{ initials(c.name) }}</div>
                                <div class="cdt__matchrow-main">
                                    <span class="cdt__mname">{{ c.name || '(no name)' }}</span>
                                    <span class="cdt__morg" *ngIf="c.org"> · {{ c.org }}</span>
                                </div>
                                <span class="cdt__mwhy">{{ c.email || 'no email' }}</span>
                            </div>
                        </div>
                        <div *ngIf="builderPreview && builderPreview.totalCount === 0" class="cdt__tiny cdt__mut cdt__matchempty">No contacts match this description yet.</div>

                        <div class="cdt__previewfoot" *ngIf="builderPreview">
                            <span class="cdt__chip">{{ builderPreview.totalCount }} total</span>
                            <span class="cdt__chip" [class.cdt__chip--ok]="builderPreview.duplicateEmailCount === 0" [class.cdt__chip--warn]="builderPreview.duplicateEmailCount > 0">{{ builderPreview.duplicateEmailCount }} duplicate email(s)</span>
                            <span class="cdt__chip" [class.cdt__chip--ok]="builderPreview.noEmailCount === 0" [class.cdt__chip--warn]="builderPreview.noEmailCount > 0">{{ builderPreview.noEmailCount }} with no email</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ════ SECTION: BINDINGS — bind view to Reply sequence ════ -->
        <section *ngIf="section === 'bindings' && screen === 'bind'" class="cdt__screen">
            <div class="cdt__eyebrow">Sync</div>
            <h1 class="cdt__title">Bind view to Reply sequence</h1>
            <p class="cdt__lede">Connect a verified AIDP view to a Reply sequence. Conduit keeps them in lockstep on your schedule — adding people who match, removing people who no longer do. You preview every change before it goes live.</p>

            <div class="cdt__twocol">
                <div>
                    <div class="cdt__card cdt__pad">
                        <h2 class="cdt__sec"><span class="cdt__num">1</span> Source view → destination sequence</h2>
                        <div class="cdt__bindrow">
                            <div class="cdt__bindcol">
                                <label class="cdt__label">Source · AIDP view</label>
                                <div *ngIf="contactViewsState.status === 'loading'" class="cdt__tiny cdt__mut"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading views…</div>
                                <div *ngIf="contactViewsState.status === 'error'" class="cdt__tiny cdt__error">{{ contactViewsState.message }}</div>
                                <div *ngIf="contactViewsState.status === 'not-deployed'" class="cdt__tiny cdt__mut">{{ contactViewsState.message }}</div>
                                <select class="cdt__input" [(ngModel)]="bindViewId" *ngIf="contactViewsState.status === 'ready'">
                                    <option [ngValue]="''" disabled>Pick a saved view…</option>
                                    <option *ngFor="let v of contactViews" [ngValue]="v.viewId">{{ v.name || v.viewId }}</option>
                                </select>
                                <button type="button" class="cdt__btn cdt__btn--ghost cdt__btn--sm" *ngIf="contactViewsState.status !== 'loading'" (click)="loadContactViews()"><i class="fa-solid fa-rotate"></i> Reload</button>
                            </div>
                            <div class="cdt__bindarrow">→</div>
                            <div class="cdt__bindcol">
                                <label class="cdt__label">Destination · Reply sequence</label>
                                <div *ngIf="bindSequencesState.status === 'loading'" class="cdt__tiny cdt__mut"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading sequences…</div>
                                <div *ngIf="bindSequencesState.status === 'error'" class="cdt__tiny cdt__error">{{ bindSequencesState.message }}</div>
                                <div *ngIf="bindSequencesState.status === 'not-deployed'" class="cdt__tiny cdt__mut">{{ bindSequencesState.message }}</div>
                                <select class="cdt__input" [(ngModel)]="bindSequenceId" *ngIf="bindSequencesState.status === 'ready'">
                                    <option [ngValue]="null" disabled>Pick a Reply sequence…</option>
                                    <option *ngFor="let s of bindSequences" [ngValue]="s.id">{{ s.name }} ({{ s.status }})</option>
                                </select>
                                <button type="button" class="cdt__btn cdt__btn--ghost cdt__btn--sm" *ngIf="bindSequencesState.status !== 'loading'" (click)="loadBindSequences()"><i class="fa-solid fa-rotate"></i> Reload</button>
                            </div>
                        </div>

                        <div class="cdt__field">
                            <label class="cdt__label">Binding name <span class="cdt__mut">(optional)</span></label>
                            <input class="cdt__input" [(ngModel)]="bindName" placeholder="Defaults to the sequence name">
                        </div>

                        <h2 class="cdt__sec"><span class="cdt__num">2</span> How often should Reply match the view?</h2>
                        <div class="cdt__freqgrid">
                            <button *ngFor="let f of freqOptions" type="button" class="cdt__freqopt" [class.is-on]="bindFrequencyHours === f.hours" (click)="bindFrequencyHours = f.hours">
                                <div class="cdt__freqopt-t">{{ f.title }}</div>
                                <div class="cdt__freqopt-d">{{ f.detail }}</div>
                            </button>
                        </div>

                        <h2 class="cdt__sec"><span class="cdt__num">3</span> Sync behavior</h2>
                        <label class="cdt__toggle">
                            <input type="checkbox" [(ngModel)]="bindAddOnMatch">
                            <span class="cdt__toggle-text"><b>Add people who newly match</b><small>Enroll them in the Reply sequence on the next run.</small></span>
                        </label>
                        <label class="cdt__toggle">
                            <input type="checkbox" [(ngModel)]="bindRemoveOnLeave">
                            <span class="cdt__toggle-text"><b>Remove people who stop matching</b><small>Pull them out of the sequence — they left the audience.</small></span>
                        </label>
                        <div class="cdt__field">
                            <label class="cdt__label">Removal mode</label>
                            <select class="cdt__input" [(ngModel)]="bindRemovalMode">
                                <option *ngFor="let m of removalModes" [value]="m">{{ m }}</option>
                            </select>
                        </div>
                        <div class="cdt__toggle cdt__toggle--locked">
                            <input type="checkbox" checked disabled>
                            <span class="cdt__toggle-text"><b>Auto-skip duplicate emails</b><small class="cdt__ok-text">Always on. One email enrolls once, even across views.</small></span>
                        </div>
                    </div>

                    <p class="cdt__error" *ngIf="bindError"><i class="fa-solid fa-triangle-exclamation"></i> {{ bindError }}</p>

                    <button class="cdt__btn" [disabled]="bindCreating || !bindViewId || bindSequenceId == null" (click)="createBindingAndDryRun()">
                        <i class="fa-solid fa-link" *ngIf="!bindCreating"></i>
                        <i class="fa-solid fa-circle-notch fa-spin" *ngIf="bindCreating"></i>
                        Create binding &amp; preview
                    </button>

                    <p class="cdt__tiny cdt__mut cdt__bindnote">
                        <i class="fa-solid fa-circle-info"></i>
                        Creating the binding saves it in <b>DryRun</b> mode and takes you to the Dry run screen. You can't go live without previewing first — the dry run is the gate.
                    </p>
                </div>

                <div>
                    <div class="cdt__card cdt__pad">
                        <b>Lockstep at a glance</b>
                        <div class="cdt__metric"><span>Source verified</span><span class="cdt__ok-text">via View builder preview</span></div>
                        <div class="cdt__metric"><span>Duplicate guard</span><span class="cdt__ok-text">on (by email)</span></div>
                        <div class="cdt__metric"><span>Replies → Activities</span><span class="cdt__ok-text">on</span></div>
                        <div class="cdt__metric"><span>First run</span><span>preview required</span></div>
                    </div>
                    <div class="cdt__banner cdt__banner--intent">
                        <i class="fa-solid fa-circle-info"></i>
                        <div><b>Saving a binding does not send anything.</b><br><span class="cdt__tiny">It schedules the sync. The first enrollment happens only after you approve the dry run.</span></div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ════ SECTION: BINDINGS — dry run ════ -->
        <section *ngIf="section === 'bindings' && screen === 'dryrun'" class="cdt__screen">
            <div class="cdt__eyebrow">Sync · preview</div>
            <h1 class="cdt__title">Dry run<span *ngIf="dryRunBinding"> — {{ dryRunBinding.name }}</span></h1>
            <p class="cdt__lede">This is exactly what the next sync would do. Nobody is added or removed until you approve it.</p>

            <div *ngIf="!dryRunBinding" class="cdt__state">
                <i class="fa-solid fa-circle-half-stroke"></i>
                <div><strong>Pick a binding</strong><p>Open a binding's Dry run from the Bindings screen.</p></div>
            </div>

            <ng-container *ngIf="dryRunBinding">
                <div *ngIf="dryRunState.status === 'loading'" class="cdt__state"><i class="fa-solid fa-circle-notch fa-spin"></i> Computing the diff (no Reply writes)…</div>
                <div *ngIf="dryRunState.status === 'not-deployed'" class="cdt__state cdt__state--info">
                    <i class="fa-solid fa-plug-circle-xmark"></i><div><strong>Reconcile action not deployed</strong><p>{{ dryRunState.message }}</p></div>
                </div>
                <div *ngIf="dryRunState.status === 'error'" class="cdt__state cdt__state--error">
                    <i class="fa-solid fa-triangle-exclamation"></i><div><strong>Dry run failed</strong><p>{{ dryRunState.message }}</p></div>
                </div>

                <ng-container *ngIf="dryRunState.status === 'ready' && dryRunResult">
                    <div class="cdt__gaugewide">
                        <div class="cdt__lockstep">
                            <i class="fa-solid fa-circle-half-stroke cdt__lockstep-ic"></i>
                            <div class="cdt__lockstep-read">
                                <b>{{ dryRunResult.wasDryRun ? 'Would change' : 'Applied' }}</b><br>
                                +{{ dryRunResult.addedCount }} added · −{{ dryRunResult.removedCount }} removed<br>
                                <span class="cdt__mut">{{ dryRunResult.errorCount }} error(s) · {{ dryRunResult.lastSyncStatus }}</span>
                            </div>
                        </div>
                        <div class="cdt__spacer"></div>
                        <span class="cdt__chip" [class.cdt__chip--paused]="dryRunResult.wasDryRun" [class.cdt__chip--flow]="!dryRunResult.wasDryRun">
                            {{ dryRunResult.wasDryRun ? 'Preview · no writes' : 'Live · applied' }}
                        </span>
                    </div>

                    <div class="cdt__diffgrid">
                        <div class="cdt__diffcol cdt__diffcol--add">
                            <div class="cdt__diffhead"><i class="fa-solid fa-plus"></i> {{ dryRunResult.wasDryRun ? 'Will be added' : 'Added' }} <span class="cdt__c">{{ dryRunResult.addedCount }}</span></div>
                            <ng-container *ngIf="dryRunResult.addSample.length > 0; else addEmpty">
                                <div class="cdt__diffrow" *ngFor="let r of dryRunResult.addSample">
                                    <span class="cdt__diffmail">{{ r.email }}</span>
                                    <span class="cdt__tiny cdt__mut">{{ r.reason }}</span>
                                </div>
                                <div class="cdt__diffrow cdt__mut cdt__tiny" *ngIf="dryRunResult.addSampleTruncated">
                                    Showing first {{ dryRunResult.addSample.length }} of {{ dryRunResult.addedCount }} — full list applies on sync.
                                </div>
                            </ng-container>
                            <ng-template #addEmpty><div class="cdt__diffrow cdt__mut">No one new matches the view — nothing to add.</div></ng-template>
                        </div>
                        <div class="cdt__diffcol cdt__diffcol--rem">
                            <div class="cdt__diffhead"><i class="fa-solid fa-minus"></i> {{ dryRunResult.wasDryRun ? 'Will be removed' : 'Removed' }} <span class="cdt__c">{{ dryRunResult.removedCount }}</span></div>
                            <ng-container *ngIf="dryRunResult.removeSample.length > 0; else remEmpty">
                                <div class="cdt__diffrow" *ngFor="let r of dryRunResult.removeSample">
                                    <span class="cdt__diffmail">{{ r.email }}</span>
                                    <span class="cdt__tiny cdt__mut">{{ r.reason }}<span *ngIf="r.statusInSequence"> · now {{ r.statusInSequence }}</span></span>
                                </div>
                                <div class="cdt__diffrow cdt__mut cdt__tiny" *ngIf="dryRunResult.removeSampleTruncated">
                                    Showing first {{ dryRunResult.removeSample.length }} of {{ dryRunResult.removedCount }} — full list applies on sync.
                                </div>
                            </ng-container>
                            <ng-template #remEmpty><div class="cdt__diffrow cdt__mut">Everyone enrolled still matches the view — nothing to remove.</div></ng-template>
                        </div>
                    </div>

                    <p class="cdt__tiny cdt__mut cdt__diffmsg">{{ dryRunResult.message }}</p>

                    <!-- The ONLY go-live gate -->
                    <div class="cdt__banner cdt__banner--intent" *ngIf="dryRunResult.wasDryRun">
                        <i class="fa-solid fa-circle-info"></i>
                        <div class="cdt__banner-main"><b>Approve to apply these changes to Reply.</b><br><span class="cdt__tiny">This is the only action in Conduit that touches Reply, and the only path that sets this binding Live.</span></div>
                        <button class="cdt__btn cdt__btn--danger" [disabled]="isApproving" (click)="approveAndGoLive()">
                            <i class="fa-solid fa-circle-check" *ngIf="!isApproving"></i>
                            <i class="fa-solid fa-circle-notch fa-spin" *ngIf="isApproving"></i>
                            Approve &amp; go live
                        </button>
                    </div>
                    <div class="cdt__banner cdt__banner--live" *ngIf="!dryRunResult.wasDryRun">
                        <i class="fa-solid fa-circle-check"></i>
                        <div>{{ approveMessage || 'Applied to Reply. The binding is now in lockstep with the view.' }}</div>
                    </div>
                    <p class="cdt__error" *ngIf="approveMessage && dryRunResult.wasDryRun"><i class="fa-solid fa-triangle-exclamation"></i> {{ approveMessage }}</p>

                    <button class="cdt__btn cdt__btn--ghost cdt__btn--sm cdt__rerun" (click)="runDryRun()"><i class="fa-solid fa-rotate"></i> Re-run dry run</button>
                </ng-container>
            </ng-container>
        </section>

        <!-- ════ SECTION: BINDINGS — run history (last-run-only, v1) ════ -->
        <section *ngIf="section === 'bindings' && screen === 'history'" class="cdt__screen">
            <div class="cdt__eyebrow">Sync</div>
            <h1 class="cdt__title">Run history</h1>
            <p class="cdt__lede">The last sync per binding, what it changed, and its status. <b>v1 shows the latest run only</b> (from each binding's LastSync* fields) — full per-run history is a fast-follow.</p>

            <div class="cdt__card">
                <div *ngIf="bindings.length === 0" class="cdt__state"><i class="fa-solid fa-rotate"></i><div><strong>No runs yet</strong></div></div>
                <div class="cdt__runrow" *ngFor="let b of bindings">
                    <div class="cdt__runwhen">{{ b.lastSyncAt ? (b.lastSyncAt | date:'short') : 'never' }}</div>
                    <div class="cdt__runpair">
                        <span class="cdt__chip cdt__chip--ok" *ngIf="(b.lastSyncAddedCount||0) > 0">+{{ b.lastSyncAddedCount }}</span>
                        <span class="cdt__chip cdt__chip--bad" *ngIf="(b.lastSyncRemovedCount||0) > 0">−{{ b.lastSyncRemovedCount }}</span>
                        <span class="cdt__chip cdt__chip--{{ statusChip(b.lastSyncStatus) }}">{{ b.lastSyncStatus || 'not run' }}</span>
                    </div>
                    <div class="cdt__runsummary"><b>{{ b.name }}</b> — {{ b.lastSyncMessage || 'no sync recorded yet.' }}</div>
                </div>
            </div>
        </section>

        <!-- ════ SECTION: ACTIVITIES — replies → activities (folded into People) ════ -->
        <section *ngIf="section === 'activities' && screen === 'activities'" class="cdt__screen">
            <div class="cdt__eyebrow">Engagement · feedback loop</div>
            <h1 class="cdt__title">Replies → Activities</h1>
            <p class="cdt__lede">When someone replies in Reply, Conduit writes it back to AIDP as an Activity on that contact. The loop closes itself.</p>

            <div class="cdt__rowbtns">
                <button class="cdt__btn" [disabled]="isSyncing" (click)="syncRepliesNow(true)"><i class="fa-solid fa-circle-half-stroke"></i> Dry-run sync</button>
                <button class="cdt__btn cdt__btn--primary" [disabled]="isSyncing" (click)="syncRepliesNow(false)">
                    <i class="fa-solid fa-reply" *ngIf="!isSyncing"></i><i class="fa-solid fa-circle-notch fa-spin" *ngIf="isSyncing"></i>
                    Sync replies now
                </button>
            </div>

            <div class="cdt__tiles" *ngIf="syncResult">
                <div class="cdt__tile"><div class="cdt__tile-lab">Events fetched</div><div class="cdt__tile-big">{{ syncResult.eventsFetched }}</div></div>
                <div class="cdt__tile"><div class="cdt__tile-lab">Activities {{ syncResult.wasDryRun ? 'would create' : 'created' }}</div><div class="cdt__tile-big">{{ syncResult.activitiesCreated }}</div></div>
                <div class="cdt__tile"><div class="cdt__tile-lab">Skipped (existing)</div><div class="cdt__tile-big">{{ syncResult.activitiesSkipped }}</div></div>
                <div class="cdt__tile"><div class="cdt__tile-lab">Errors</div><div class="cdt__tile-big">{{ syncResult.errorCount }}</div></div>
            </div>
            <p class="cdt__tiny cdt__mut" *ngIf="syncResult">{{ syncResult.summary }}</p>

            <h2 class="cdt__sec"><i class="fa-solid fa-reply"></i> Recent reply &amp; bounce activities</h2>
            <div *ngIf="activitiesState.status === 'loading'" class="cdt__state"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading activities…</div>
            <div *ngIf="activitiesState.status === 'not-deployed'" class="cdt__state cdt__state--info"><i class="fa-solid fa-plug-circle-xmark"></i><div><strong>Action not deployed</strong><p>{{ activitiesState.message }}</p></div></div>
            <div *ngIf="activitiesState.status === 'error'" class="cdt__state cdt__state--error"><i class="fa-solid fa-triangle-exclamation"></i><div><strong>Couldn't load activities</strong><p>{{ activitiesState.message }}</p></div></div>

            <div class="cdt__card cdt__pad" *ngIf="activitiesState.status === 'ready'">
                <div *ngIf="activities.length === 0" class="cdt__tiny cdt__mut">No reply or bounce activities recorded yet. Run a sync above.</div>
                <div class="cdt__activityrow" *ngFor="let a of activities">
                    <div class="cdt__mav cdt__mav--lg">{{ initials(a.title) }}</div>
                    <div class="cdt__activitymain">
                        <div class="cdt__quote">{{ a.description || a.title }}</div>
                        <div class="cdt__activitymeta">
                            <b>{{ a.title }}</b>
                            <span class="cdt__chip" [class.cdt__chip--ok]="a.isReplied" [class.cdt__chip--bad]="a.isBounced">{{ a.isReplied ? 'Replied' : (a.isBounced ? 'Bounced' : a.type) }}</span>
                            <span class="cdt__kv">{{ a.activityDate ? (a.activityDate | date:'short') : '' }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ════ SECTION: HEALTH — integration health ════ -->
        <section *ngIf="section === 'health' && screen === 'health'" class="cdt__screen">
            <div class="cdt__eyebrow">Data platform</div>
            <h1 class="cdt__title">Integration health</h1>
            <p class="cdt__lede">AIDP is only as good as the data flowing in. Conduit watches whether each integration ran, finished, and how fresh it is — so we never email off stale data.</p>

            <div class="cdt__rowbtns">
                <label class="cdt__label cdt__inline">Staleness threshold (hours)</label>
                <input class="cdt__input cdt__input--sm" type="number" min="1" [(ngModel)]="stalenessThresholdHours">
                <button class="cdt__btn" (click)="loadHealth()"><i class="fa-solid fa-rotate"></i> Refresh</button>
            </div>

            <div *ngIf="healthState.status === 'loading'" class="cdt__state"><i class="fa-solid fa-circle-notch fa-spin"></i> Checking integrations…</div>
            <div *ngIf="healthState.status === 'not-deployed'" class="cdt__state cdt__state--info"><i class="fa-solid fa-plug-circle-xmark"></i><div><strong>Action not deployed</strong><p>{{ healthState.message }}</p></div></div>
            <div *ngIf="healthState.status === 'error'" class="cdt__state cdt__state--error"><i class="fa-solid fa-triangle-exclamation"></i><div><strong>Couldn't load health</strong><p>{{ healthState.message }}</p></div></div>

            <ng-container *ngIf="healthState.status === 'ready' && health">
                <div class="cdt__tiles cdt__tiles--3">
                    <div class="cdt__tile"><div class="cdt__tile-lab">Integrations</div><div class="cdt__tile-big">{{ health.integrationCount }}</div></div>
                    <div class="cdt__tile"><div class="cdt__tile-lab">Stale</div><div class="cdt__tile-big" [class.cdt__warn-text]="health.staleCount > 0">{{ health.staleCount }}</div></div>
                    <div class="cdt__tile"><div class="cdt__tile-lab">Errored</div><div class="cdt__tile-big" [class.cdt__err-text]="health.erroredCount > 0">{{ health.erroredCount }}</div></div>
                </div>

                <div class="cdt__intgrid">
                    <div class="cdt__intcard" *ngFor="let i of health.integrations">
                        <div class="cdt__inthead">
                            <div class="cdt__intlogo"><i class="fa-solid fa-database"></i></div>
                            <div class="cdt__intmain"><div class="cdt__intname">{{ i.integration }}</div><div class="cdt__intsub">{{ i.company }}</div></div>
                            <span class="cdt__chip cdt__chip--{{ healthChip(i) }}">{{ i.errored ? 'Errored' : (i.isStale ? 'Stale' : 'Healthy') }}</span>
                        </div>
                        <div class="cdt__intbody">
                            <div class="cdt__metric"><span>Last completed</span><span class="cdt__kv">{{ i.lastRunAt ? (i.lastRunAt | date:'short') : '—' }}</span></div>
                            <div class="cdt__metric"><span>Age</span><span class="cdt__kv">{{ i.ageHours !== null ? (i.ageHours + 'h') : '—' }}</span></div>
                            <div class="cdt__intstages">
                                <div class="cdt__stage"><span class="cdt__sd" [class.cdt__sd--ok]="!!i.lastRunAt" [class.cdt__sd--bad]="!i.lastRunAt"></span> Ran on schedule</div>
                                <div class="cdt__stage"><span class="cdt__sd" [class.cdt__sd--ok]="i.finished" [class.cdt__sd--warn]="!i.finished"></span> Completed fully</div>
                                <div class="cdt__stage"><span class="cdt__sd" [class.cdt__sd--ok]="!i.errored" [class.cdt__sd--bad]="i.errored"></span> No errors</div>
                            </div>
                            <div class="cdt__metric" *ngIf="i.totalRecords !== null"><span>Records</span><span class="cdt__kv">{{ i.totalRecords }}</span></div>
                        </div>
                    </div>
                </div>
                <div *ngIf="health.integrations.length === 0" class="cdt__state"><i class="fa-solid fa-database"></i><div><strong>No integration runs observed</strong></div></div>
            </ng-container>
        </section>

        </div>
    </div>
</div>
